import { useEffect as useEffectA, useMemo as useMemoA, useRef as useRefA, useState as useStateA } from "react";
import { Api } from "./api";
import { Icon, Sparkline, Stat, TrendChart } from "./components";

// Super-admin (SaaS operator) experience: overview, communities, revenue, trends.
// All numbers come from the Flask backend at /api/dashboard/*.

// Generic "fetch on mount" helper. Returns {data, error, loading}.
function useDashboardCall(loader, deps = []) {
  const [state, setState] = useStateA({ data: null, error: null, loading: true });
  useEffectA(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    loader()
      .then((data) => { if (!cancelled) setState({ data, error: null, loading: false }); })
      .catch((err) => { if (!cancelled) setState({ data: null, error: err.message || String(err), loading: false }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

const fmtUSD = (n) => "$" + Math.round(n).toLocaleString();
const fmtCentsAsUSD = (c) => fmtUSD((c || 0) / 100);
const fmtNum = (n) => (n || 0).toLocaleString();
const fmtTokens = (n) => {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return String(n);
};
const fmtTokensCompact = (n) => (!n ? "—" : fmtTokens(n));
const fmtUsdCompact = (v) => {
  if (v === null || v === undefined || v === 0) return "—";
  if (v < 0.01) return "<$0.01";
  if (v < 1) return "$" + v.toFixed(3);
  if (v < 1000) return "$" + v.toFixed(2);
  return "$" + v.toFixed(0);
};

const TIER_LABEL = { trial: "Trial", starter: "Starter", pro: "Pro", enterprise: "Enterprise" };
const TIER_COLOR = {
  trial: "var(--hairline-2)",
  starter: "var(--secondary)",
  pro: "var(--accent)",
  enterprise: "var(--ink)",
};
const TIER_PILL = {
  trial: "pill--ghost",
  starter: "pill--green",
  pro: "pill--gold",
  enterprise: "pill--accent",
};
const tierLabel = (t) => TIER_LABEL[t] || (t ? t[0].toUpperCase() + t.slice(1) : "—");

function CardSkeleton({ height = 200, label = "Loading…" }) {
  return (
    <div className="card card--pad-lg" style={{ minHeight: height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--mute)" }}>
      {label}
    </div>
  );
}

function CardError({ message, height = 200 }) {
  return (
    <div className="card card--pad-lg" style={{ minHeight: height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--danger)" }}>
      Couldn't load: {message}
    </div>
  );
}

function CardEmpty({ message, height = 200 }) {
  return (
    <div className="card card--pad-lg" style={{ minHeight: height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--mute)" }}>
      {message}
    </div>
  );
}

/* ─── AdminOverview ──────────────────────────────────────────────────────── */
function AdminOverview() {
  const ov = useDashboardCall(() => Api.dashboardOverview());
  const tr = useDashboardCall(() => Api.dashboardTrends({ days: 30 }));
  const rv = useDashboardCall(() => Api.dashboardRevenue());
  const cm = useDashboardCall(() => Api.dashboardCommunities());

  return (
    <div className="scroll">
      <div className="stack-lg">
        {/* MRR feature card */}
        {ov.loading && <CardSkeleton height={320} label="Loading overview…" />}
        {ov.error && <CardError message={ov.error} height={320} />}
        {ov.data && (
          <div className="card card--feature card--pad-lg" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 32, alignItems: "stretch" }}>
            <div>
              <div className="card__title">Monthly recurring revenue</div>
              <div className="stat__value" style={{ fontSize: 64, marginTop: 14 }}>
                <span>{fmtCentsAsUSD(ov.data.revenue.monthly_recurring_cents)}</span>
              </div>
              <div className="text-sm text-mute" style={{ marginTop: 4 }}>
                Communities {fmtCentsAsUSD(ov.data.revenue.from_communities_cents)} · Companies {fmtCentsAsUSD(ov.data.revenue.from_companies_cents)}
              </div>
              <hr className="divider" />
              <div className="col" style={{ gap: 10 }}>
                {[
                  ["Active communities", fmtNum(ov.data.communities)],
                  ["Property mgmt companies", fmtNum(ov.data.property_management_companies)],
                  ["Total users", fmtNum(ov.data.users)],
                ].map(([l, v]) => (
                  <div key={l} className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
                    <span className="text-sm text-mute">{l}</span>
                    <span className="display" style={{ fontFamily: "var(--font-display)", fontSize: 19, fontWeight: 400 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ borderLeft: "1px solid var(--hairline)", paddingLeft: 32, display: "flex", flexDirection: "column" }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div className="card__title">Questions answered · last 30 days</div>
              </div>
              <TrendsBlock state={tr} />
              <div className="row" style={{ marginTop: "auto", paddingTop: 14, gap: 32, fontSize: 12.5 }}>
                <div>
                  <div className="text-mute">Total questions</div>
                  <div className="display" style={{ fontSize: 24, marginTop: 2, fontWeight: 400 }}>{fmtNum(ov.data.questions_asked_total)}</div>
                </div>
                <div>
                  <div className="text-mute">Tokens</div>
                  <div className="display" style={{ fontSize: 24, marginTop: 2, fontWeight: 400 }}>{fmtTokens(ov.data.tokens.total)}</div>
                </div>
                <div>
                  <div className="text-mute">Estimated cost</div>
                  <div className="display" style={{ fontSize: 24, marginTop: 2, fontWeight: 400 }}>${(ov.data.tokens.estimated_cost_usd || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending unit-count decrease requests (PR 6). Operator
            approval queue. Empty by default; only renders if there
            are pending rows. */}
        <PendingUnitCountChanges />

        {/* Two columns: revenue-by-tier, top communities. RecentEvents removed
            — no backend event stream yet. */}
        <div className="grid" style={{ gridTemplateColumns: "1fr 1.4fr" }}>
          <RevenueByTier state={rv} />
          <TopCommunities state={cm} />
        </div>
      </div>
    </div>
  );
}


/* ─── Pending unit-count decrease requests ─────────────────────────────── */
// Operator queue: managers who tried to reduce their community's
// household count have their requests pile up here. Approve applies
// the new value + pushes the new quantity to Stripe; deny leaves the
// community's unit_count unchanged. Hidden entirely when no pending
// requests exist so the dashboard doesn't carry dead UI.
function PendingUnitCountChanges() {
  const [rows, setRows] = useStateA([]);
  const [loading, setLoading] = useStateA(true);
  const [error, setError] = useStateA(null);
  const [busy, setBusy] = useStateA(null); // `${id}:approve` | `${id}:deny`

  const reload = () => {
    setLoading(true);
    setError(null);
    Api.adminListUnitCountChanges("pending")
      .then((data) => { setRows(data || []); setLoading(false); })
      .catch((err) => { setError(err.message || "Couldn't load."); setLoading(false); });
  };
  useEffectA(() => { reload(); }, []);

  const decide = async (id, action) => {
    const note = window.prompt(
      action === "approve"
        ? "Optional note for audit (visible to the manager):"
        : "Why are you denying this? (visible to the manager)",
      "",
    );
    // Cancel on the prompt should abort the action entirely — for
    // approve too, otherwise hitting Cancel still flips the request
    // to approved + syncs Stripe. (Codex P2 catch on PR #100.)
    // window.prompt returns null for cancel, "" for OK-with-empty.
    if (note === null) return;
    setBusy(`${id}:${action}`);
    setError(null);
    try {
      if (action === "approve") {
        await Api.adminApproveUnitCountChange(id, note || null);
      } else {
        await Api.adminDenyUnitCountChange(id, note || null);
      }
      reload();
    } catch (err) {
      setError(err.message || "Couldn't decide.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return null;        // Skip the empty skeleton on initial load
  if (error) {
    return (
      <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>{error}</div>
    );
  }
  if (rows.length === 0) return null;  // No queue, no UI

  return (
    <div className="card card--pad-lg" style={{ border: "1px solid var(--accent, #c25e3a)", background: "var(--accent-soft)" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div className="card__title" style={{ margin: 0 }}>
          Pending household-count decreases
        </div>
        <div className="text-sm text-mute">{rows.length} awaiting approval</div>
      </div>
      <div className="col" style={{ gap: 12 }}>
        {rows.map((r, i) => {
          const delta = r.delta; // negative for decreases
          return (
            <div key={r.id} className="row" style={{
              justifyContent: "space-between",
              padding: "10px 0",
              borderTop: i > 0 ? "1px dashed var(--hairline)" : 0,
              gap: 12,
              alignItems: "flex-start",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  {r.community?.name || `Community ${r.community?.id}`}{" "}
                  <span className="text-mute text-sm" style={{ fontWeight: 400 }}>
                    {r.community?.slug && `· ${r.community.slug}`}
                  </span>
                </div>
                <div className="text-sm" style={{ marginTop: 2 }}>
                  <b>{r.current_count.toLocaleString()}</b>{" → "}
                  <b style={{ color: "var(--danger)" }}>{r.requested_count.toLocaleString()}</b>{" "}
                  <span className="text-mute">({delta})</span>
                </div>
                {r.reason && (
                  <div className="text-xs text-mute" style={{ marginTop: 4 }}>
                    Reason: {r.reason}
                  </div>
                )}
                <div className="text-xs text-mute" style={{ marginTop: 4 }}>
                  Requested by {r.requested_by?.full_name || r.requested_by?.email || "—"}
                  {r.created_at && ` · ${new Date(r.created_at).toLocaleDateString()}`}
                </div>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => decide(r.id, "approve")}
                  disabled={busy != null}
                >
                  {busy === `${r.id}:approve` ? "…" : "Approve"}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => decide(r.id, "deny")}
                  disabled={busy != null}
                  style={{ color: "var(--danger)" }}
                >
                  {busy === `${r.id}:deny` ? "…" : "Deny"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendsBlock({ state }) {
  if (state.loading) return <div style={{ minHeight: 240, display: "flex", alignItems: "center", color: "var(--mute)" }}>Loading trend…</div>;
  if (state.error) return <div style={{ minHeight: 240, display: "flex", alignItems: "center", color: "var(--danger)" }}>{state.error}</div>;
  const series = state.data?.series || [];
  if (series.length === 0) {
    return (
      <div style={{ minHeight: 240, display: "flex", alignItems: "center", color: "var(--mute)" }}>
        No activity in the last {state.data?.window_days || 30} days yet.
      </div>
    );
  }
  const _dayTokens = (d) => (
    (d.input_tokens || 0) + (d.output_tokens || 0)
    + (d.cache_read_input_tokens || 0) + (d.cache_creation_input_tokens || 0)
  );
  if (series.length < 2) {
    // TrendChart divides by (length - 1); a single data point would render
    // as an infinitely-stretched line. Show the value plainly instead.
    const d = series[0];
    return (
      <div style={{ minHeight: 240, display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center", gap: 6, color: "var(--mute)" }}>
        <span>One day of activity so far:</span>
        <span className="mono" style={{ color: "var(--ink)" }}>{d.date} · {d.chat_events} chats · {_dayTokens(d).toLocaleString()} tokens</span>
      </div>
    );
  }
  const data = series.map((d) => [
    d.chat_events,
    _dayTokens(d) / 1000,
  ]);
  return <TrendChart data={data} height={240} />;
}

function RevenueByTier({ state }) {
  if (state.loading) return <CardSkeleton height={220} label="Loading revenue…" />;
  if (state.error) return <CardError message={state.error} height={220} />;
  const rows = state.data?.by_tier || [];
  if (rows.length === 0) {
    return <CardEmpty message="No communities yet." height={220} />;
  }
  const total = rows.reduce((a, r) => a + r.monthly_revenue_cents, 0);
  const totalC = rows.reduce((a, r) => a + r.communities, 0);
  return (
    <div className="card">
      <div className="card__hd">
        <div className="card__title">Revenue by tier</div>
        <span className="text-sm text-mute">monthly</span>
      </div>
      <div style={{ display: "flex", height: 14, borderRadius: 4, overflow: "hidden", marginBottom: 14, background: "var(--bg-2)" }}>
        {total > 0 && rows.map((r) => (
          <div
            key={r.tier}
            style={{ width: `${(r.monthly_revenue_cents / total) * 100}%`, background: TIER_COLOR[r.tier] || "var(--mute)" }}
            title={`${tierLabel(r.tier)}: ${fmtCentsAsUSD(r.monthly_revenue_cents)}`}
          />
        ))}
      </div>
      <div className="col" style={{ gap: 10 }}>
        {rows.map((r) => (
          <div key={r.tier} className="row" style={{ justifyContent: "space-between" }}>
            <div className="row" style={{ gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: TIER_COLOR[r.tier] || "var(--mute)" }} />
              <span style={{ fontWeight: 500, fontSize: 13.5 }}>{tierLabel(r.tier)}</span>
              <span className="text-sm text-mute">· {r.communities}</span>
            </div>
            <span className="mono text-sm">{fmtCentsAsUSD(r.monthly_revenue_cents)}</span>
          </div>
        ))}
        <hr className="divider" style={{ margin: "6px 0" }} />
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="text-sm text-mute">{totalC} {totalC === 1 ? "community" : "communities"}</span>
          <span className="display" style={{ fontSize: 18, fontWeight: 500 }}>{fmtCentsAsUSD(total)}/mo</span>
        </div>
      </div>
    </div>
  );
}

function TopCommunities({ state }) {
  if (state.loading) return <CardSkeleton height={220} label="Loading communities…" />;
  if (state.error) return <CardError message={state.error} height={220} />;
  const all = state.data || [];
  if (all.length === 0) {
    return <CardEmpty message="No communities onboarded yet." height={220} />;
  }
  const _sumTokens = (c) => (
    c.input_tokens + c.output_tokens
    + (c.cache_read_input_tokens || 0) + (c.cache_creation_input_tokens || 0)
  );
  const top = [...all].sort((a, b) => _sumTokens(b) - _sumTokens(a)).slice(0, 6);
  const max = _sumTokens(top[0]) || 1;
  return (
    <div className="card">
      <div className="card__hd">
        <div className="card__title">Top communities by usage</div>
        <span className="text-sm text-mute">tokens · all-time</span>
      </div>
      <div className="col" style={{ gap: 12 }}>
        {top.map((c) => {
          const tokens = _sumTokens(c);
          return (
            <div key={c.id}>
              <div className="row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{c.name}</span>
                <span className="text-mono text-sm" style={{ color: "var(--mute)" }}>{fmtTokens(tokens)}</span>
              </div>
              <div style={{ height: 6, background: "var(--bg-2)", borderRadius: 3 }}>
                <div style={{ height: "100%", width: `${(tokens / max) * 100}%`, background: TIER_COLOR[c.tier] || "var(--accent)", borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Communities table + drill-down ─────────────────────────────────────── */
function AdminCommunities({ user }) {
  const [q, setQ] = useStateA("");
  const [statusFilter, setStatusFilter] = useStateA("all");
  const [openId, setOpenId] = useStateA(null);
  const [reloadKey, setReloadKey] = useStateA(0);
  const cm = useDashboardCall(() => Api.dashboardCommunities(), [reloadKey]);

  const rows = useMemoA(() => {
    const all = cm.data || [];
    let out = all;
    if (statusFilter !== "all") {
      out = out.filter((c) => {
        if (statusFilter === "none") return !c.subscription_status;
        return c.subscription_status === statusFilter;
      });
    }
    if (q) {
      const needle = q.toLowerCase();
      out = out.filter(
        (c) =>
          c.name.toLowerCase().includes(needle) ||
          (c.slug || "").includes(needle),
      );
    }
    return out;
  }, [cm.data, q, statusFilter]);

  if (openId) {
    return (
      <CommunityDetail
        communityId={openId}
        user={user}
        onBack={() => setOpenId(null)}
        onChanged={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <div className="scroll">
      <div className="stack-md">
        <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
          <div className="card" style={{ padding: "8px 12px", flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
            <Icon name="search" size={16} style={{ color: "var(--mute)" }} />
            <input className="input" aria-label="Search communities" placeholder="Search communities…" value={q} onChange={(e) => setQ(e.target.value)} style={{ border: 0, padding: 0, background: "transparent" }} />
          </div>
          <select
            className="input"
            aria-label="Filter by subscription status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="past_due">Past due</option>
            <option value="canceled">Canceled</option>
            <option value="none">No status</option>
          </select>
        </div>

        {cm.loading && <CardSkeleton height={240} label="Loading communities…" />}
        {cm.error && <CardError message={cm.error} height={240} />}
        {cm.data && rows.length === 0 && (
          <CardEmpty message={q ? `No matches for "${q}"` : "No communities onboarded yet."} height={240} />
        )}
        {cm.data && rows.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Community</th>
                  <th>Slug</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Questions</th>
                  <th>Tokens</th>
                  <th>Est. cost</th>
                  <th>MRR</th>
                  <th>Last active</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const initials = c.name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setOpenId(c.id)}
                      style={{ cursor: "pointer", opacity: c.is_active === false ? 0.55 : 1 }}
                      title="Open community"
                    >
                      <td>
                        <div className="row">
                          <span className="avatar avatar--t" style={{ background: "var(--secondary-soft)", color: "var(--secondary)" }}>
                            {initials || "?"}
                          </span>
                          <span style={{ fontWeight: 600 }}>{c.name}</span>
                          {c.is_active === false && <span className="pill pill--ghost" style={{ marginLeft: 8 }}>Deactivated</span>}
                        </div>
                      </td>
                      <td className="mono text-sm text-mute">{c.slug}</td>
                      <td><span className={`pill ${TIER_PILL[c.tier] || "pill--ghost"}`}>{tierLabel(c.tier)}</span></td>
                      <td>
                        <span
                          className={`pill ${
                            c.subscription_status === "active" || c.subscription_status === "trialing"
                              ? "pill--ok"
                              : c.subscription_status === "past_due"
                                ? "pill--warn"
                                : c.subscription_status === "canceled"
                                  ? "pill--danger"
                                  : "pill--ghost"
                          }`}
                        >
                          {c.subscription_status || "—"}
                        </span>
                      </td>
                      <td className="mono">{fmtNum(c.questions_asked)}</td>
                      <td className="mono">{fmtNum(
                        c.input_tokens + c.output_tokens
                        + (c.cache_read_input_tokens || 0)
                        + (c.cache_creation_input_tokens || 0)
                      )}</td>
                      <td className="mono">{fmtUsdCompact(c.estimated_cost_usd)}</td>
                      <td className="mono">{fmtCentsAsUSD(c.monthly_revenue_cents)}</td>
                      <td className="mono text-sm text-mute">{_fmtRelTime(c.last_active_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Operator-only polish-usage panel on the community detail page.
// Shows current month's polish meter (X of Y used) and lets the
// operator forgive a specific count or reset the whole window. The
// backend hard-deletes the matching TokenUsageEvent rows, so the
// manager-side polish meter updates immediately.
function PolishUsageCard({ communityId }) {
  const [state, setState] = useStateA({ loading: true, error: null, data: null });
  const [forgiveN, setForgiveN] = useStateA("");
  const [busy, setBusy] = useStateA(false);
  const [error, setError] = useStateA(null);
  const [okMsg, setOkMsg] = useStateA(null);

  const reload = () => {
    setState({ loading: true, error: null, data: null });
    setError(null);
    Api.adminGetPolishUsage(communityId)
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load polish usage.", data: null }));
  };

  useEffectA(() => { reload(); }, [communityId]);

  const adjust = async (body, prompt) => {
    if (prompt && !window.confirm(prompt)) return;
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      const res = await Api.adminAdjustPolishUsage(communityId, body);
      setState({ loading: false, error: null, data: res.status });
      const removed = res.removed || 0;
      setOkMsg(
        removed === 0
          ? "Nothing to forgive — no polishes in the current window."
          : `Forgave ${removed} polish${removed === 1 ? "" : "es"}. Meter updated.`
      );
      setForgiveN("");
    } catch (err) {
      setError(err.message || "Couldn't adjust polish usage.");
    } finally {
      setBusy(false);
    }
  };

  const forgive = () => {
    const n = parseInt(forgiveN, 10);
    if (!Number.isFinite(n) || n <= 0) {
      setError("Enter a positive number.");
      return;
    }
    adjust({ action: "remove", count: n }, `Forgive ${n} polish${n === 1 ? "" : "es"} for this community? This deletes the ${n} most-recent polish event${n === 1 ? "" : "s"} in the current window.`);
  };

  const resetAll = () => {
    adjust({ action: "reset" }, "Reset all polish usage for this community? Every polish event in the current 30-day window will be deleted.");
  };

  const d = state.data;
  const pct = d ? Math.min(100, Math.round((d.used / Math.max(1, d.limit)) * 100)) : 0;
  const resetAtNice = d?.reset_at
    ? new Date(d.reset_at).toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : null;

  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <h3 className="display" style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Polish usage</h3>
        {d && (
          <span className="text-sm text-mute mono">
            {d.used} / {d.limit} this month · {d.remaining} remaining
          </span>
        )}
      </div>
      <p className="text-mute text-sm" style={{ marginTop: 4 }}>
        Forgive some or all of this community's polish events in the current
        30-day window. Useful for goodwill credits or cleaning up demo runs.
      </p>

      {state.loading && <div className="text-mute text-sm" style={{ marginTop: 12 }}>Loading…</div>}
      {state.error && <div className="text-sm" style={{ marginTop: 12, color: "var(--danger)" }}>{state.error}</div>}

      {d && (
        <>
          <div style={{
            marginTop: 14, height: 8, borderRadius: 999,
            background: "var(--hairline)", overflow: "hidden",
          }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: pct >= 90 ? "var(--danger)" : "var(--accent)",
              transition: "width .2s ease",
            }} />
          </div>
          {resetAtNice && (
            <div className="text-sm text-mute" style={{ marginTop: 6 }}>
              Oldest polish rolls off {resetAtNice}.
            </div>
          )}

          <div className="row" style={{ marginTop: 16, gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ width: 140 }}>
              <label className="field__label" style={{ display: "block", marginBottom: 6 }}>Forgive count</label>
              <input
                className="input"
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                value={forgiveN}
                onChange={(e) => setForgiveN(e.target.value)}
                placeholder="e.g. 5"
                disabled={busy || d.used === 0}
              />
            </div>
            <button
              type="button"
              className="btn btn--primary"
              onClick={forgive}
              disabled={busy || d.used === 0 || !forgiveN}
              style={{ height: 38 }}
            >
              {busy ? "Forgiving…" : "Forgive polishes"}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={resetAll}
              disabled={busy || d.used === 0}
              style={{ height: 38, color: "var(--accent-ink)" }}
            >
              {busy ? "Resetting…" : `Reset all (${d.used})`}
            </button>
          </div>

          {error && (
            <div role="alert" style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)", fontSize: 13.5 }}>
              {error}
            </div>
          )}
          {okMsg && (
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "var(--secondary-soft)", color: "var(--secondary)", fontSize: 13.5 }}>
              {okMsg}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function CommunityDetail({ communityId, user: me, onBack, onChanged }) {
  const [c, setC] = useStateA({ loading: true, error: null, data: null });
  const [u, setU] = useStateA({ loading: true, error: null, users: [] });
  const [pendingUserId, setPendingUserId] = useStateA(null);
  const [confirmDelete, setConfirmDelete] = useStateA(false);
  const [busy, setBusy] = useStateA(false);
  const [actionError, setActionError] = useStateA(null);
  // Permanent-delete state (only meaningful on already-deactivated communities)
  const [permDeleteOpen, setPermDeleteOpen] = useStateA(false);
  const [permDeleteInput, setPermDeleteInput] = useStateA("");

  // Add-member form state
  const [inviteEmail, setInviteEmail] = useStateA("");
  const [inviteRole, setInviteRole] = useStateA("manager");
  const [inviteBusy, setInviteBusy] = useStateA(false);
  const [inviteError, setInviteError] = useStateA(null);
  const [inviteOk, setInviteOk] = useStateA(null);

  const reload = () => {
    setC({ loading: true, error: null, data: null });
    setU({ loading: true, error: null, users: [] });
    Api.adminGetCommunity(communityId)
      .then((data) => setC({ loading: false, error: null, data }))
      .catch((err) => setC({ loading: false, error: err.message || "Couldn't load community.", data: null }));
    Api.adminListCommunityUsers(communityId)
      .then((users) => setU({ loading: false, error: null, users: users || [] }))
      .catch((err) => setU({ loading: false, error: err.message || "Couldn't load roster.", users: [] }));
  };

  const addMember = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim();
    if (!email || inviteBusy) return;
    setInviteBusy(true);
    setInviteError(null);
    setInviteOk(null);
    try {
      const added = await Api.adminAddCommunityMember(communityId, { email, role: inviteRole });
      setInviteOk(`Added ${added.email} as ${added.role.replace("_", " ")}.`);
      setInviteEmail("");
      // Reload the roster so the new row appears with usage etc.
      Api.adminListCommunityUsers(communityId)
        .then((users) => setU({ loading: false, error: null, users: users || [] }));
    } catch (err) {
      setInviteError(err.message || "Couldn't add member.");
    } finally {
      setInviteBusy(false);
    }
  };

  const removeMember = async (user) => {
    const isSelf = me && user.id === me.id;
    const prompt = isSelf
      ? `Remove yourself from this community? Your operator console access stays intact — you can re-add yourself any time.`
      : `Remove ${user.full_name || user.email} from this community? Their account stays intact; they'll just lose access to this HOA.`;
    if (!window.confirm(prompt)) return;
    setPendingUserId(user.id);
    setActionError(null);
    try {
      await Api.adminRemoveCommunityMember(communityId, user.id);
      setU((s) => ({ ...s, users: s.users.filter((x) => x.id !== user.id) }));
    } catch (err) {
      // The backend refuses to drop the user's sole membership
      // (would orphan them). Make the next step obvious instead of
      // leaving the operator stuck — point them at Delete.
      const msg = err?.code === "only_membership"
        ? "This is the user's only community. Use Delete account to remove them entirely."
        : (err.message || "Couldn't remove member.");
      setActionError(msg);
    } finally {
      setPendingUserId(null);
    }
  };

  const deleteAccount = async (user) => {
    const isSelf = me && user.id === me.id;
    if (isSelf) {
      setActionError("You can't delete your own account here. Sign in as another operator first.");
      return;
    }
    if (!window.confirm(
      `Permanently delete ${user.full_name || user.email}? This removes the account from the platform — all memberships, chat sessions, and household links are dropped. Their attribution on bylaws / events / token usage is anonymized but those records stay.`,
    )) return;
    setPendingUserId(user.id);
    setActionError(null);
    try {
      await Api.adminDeleteUser(user.id);
      setU((s) => ({ ...s, users: s.users.filter((x) => x.id !== user.id) }));
    } catch (err) {
      setActionError(err.message || "Couldn't delete account.");
    } finally {
      setPendingUserId(null);
    }
  };

  const reactivateUser = async (user) => {
    if (!window.confirm(
      `Reactivate ${user.full_name || user.email}? Login will work again; their existing memberships still apply. Chat history wiped at deactivation cannot be restored.`,
    )) return;
    setPendingUserId(user.id);
    setActionError(null);
    try {
      await Api.adminReactivateUser(communityId, user.id);
      setU((s) => ({ ...s, users: s.users.map((x) => (x.id === user.id ? { ...x, is_active: true } : x)) }));
    } catch (err) {
      setActionError(err.message || "Couldn't reactivate user.");
    } finally {
      setPendingUserId(null);
    }
  };

  useEffectA(() => { reload(); }, [communityId]);

  const deactivateUser = async (user) => {
    if (!window.confirm(`Deactivate ${user.full_name || user.email}? Their account will be disabled and chat history wiped. Reversible by an operator with DB access.`)) return;
    setPendingUserId(user.id);
    setActionError(null);
    try {
      await Api.adminDeactivateUser(communityId, user.id);
      setU((s) => ({ ...s, users: s.users.map((x) => (x.id === user.id ? { ...x, is_active: false } : x)) }));
    } catch (err) {
      setActionError(err.message || "Couldn't deactivate user.");
    } finally {
      setPendingUserId(null);
    }
  };

  // Operator can set any role on anyone else. Editing your own role is
  // hidden so you can't lock yourself out of the operator console.
  const canEditRole = (target) => me && target.id !== me.id;
  // Remove-from-community is gentler than deactivate — the account
  // survives. Operators (super_admin) can self-remove EXCEPT when
  // that membership is their last super_admin standing — the backend
  // rejects that case with self_lockout (admin/routes.py
  // remove_community_member), so don't advertise an action the API
  // will refuse. (Codex PR #106 P2 follow-up.)
  const canRemove = (target) => {
    if (!me) return false;
    if (target.id !== me.id) return true;
    if (me.role !== "super_admin") return false;
    const memberships = me.memberships || [];
    const here = memberships.find((m) => m.community_id === communityId);
    if (!here || here.role !== "super_admin") return true;
    return memberships.some(
      (m) => m.community_id !== communityId && m.role === "super_admin",
    );
  };
  // Deactivate stays strict: never self-deactivate, even as operator.
  const canDeactivateRow = (target) => !!me && target.id !== me.id && target.is_active;
  // Reactivate mirrors deactivate's self-guard and only renders on
  // already-deactivated rows.
  const canReactivateRow = (target) => !!me && target.id !== me.id && !target.is_active;
  // Hard delete is operator-only and never self-targets (the backend
  // refuses self-delete with 400 anyway — hide the button so the
  // operator isn't tempted to click it).
  const canDeleteAccount = (target) => !!me && target.id !== me.id;
  const changeRole = async (user, nextRole) => {
    if (nextRole === user.role) return;
    setPendingUserId(user.id);
    setActionError(null);
    try {
      const updated = await Api.adminSetCommunityUserRole(communityId, user.id, nextRole);
      setU((s) => ({ ...s, users: s.users.map((x) => (x.id === user.id ? { ...x, role: updated.role } : x)) }));
    } catch (err) {
      setActionError(err.message || "Couldn't change role.");
    } finally {
      setPendingUserId(null);
    }
  };

  const deactivateCommunity = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy(true);
    setActionError(null);
    try {
      const res = await Api.adminDeactivateCommunity(communityId);
      onChanged && onChanged();
      const msg = res?.stripe_subscription_canceled
        ? `Deactivated. Stripe subscription ${res.stripe_subscription_canceled} canceled.`
        : "Deactivated.";
      alert(msg);
      onBack();
    } catch (err) {
      setActionError(err.message || "Couldn't deactivate community.");
      setBusy(false);
    }
  };

  const reactivateCommunity = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await Api.adminReactivateCommunity(communityId);
      onChanged && onChanged();
      const note = res?.restored_users ? ` Restored ${res.restored_users} member account${res.restored_users === 1 ? "" : "s"}.` : "";
      alert("Community restored." + note + " (Stripe subscription is not auto-resumed; a manager can start a new one in Settings → Plan & billing.)");
      reload();
    } catch (err) {
      setActionError(err.message || "Couldn't restore community.");
    } finally {
      setBusy(false);
    }
  };

  const permanentlyDeleteCommunity = async () => {
    if (permDeleteInput !== "DELETE") return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await Api.adminPermanentlyDeleteCommunity(communityId);
      onChanged && onChanged();
      const parts = ["Permanently deleted."];
      if (res?.stripe_subscription_canceled) parts.push(`Stripe sub ${res.stripe_subscription_canceled} canceled.`);
      if (res?.claude_files_attempted) parts.push(`${res.claude_files_attempted} Claude file deletion(s) queued.`);
      alert(parts.join(" "));
      onBack();
    } catch (err) {
      setActionError(err.message || "Couldn't permanently delete.");
      setBusy(false);
    }
  };

  return (
    <div className="scroll">
      <div className="stack-md">
        <button className="btn btn--ghost btn--sm" onClick={onBack} style={{ alignSelf: "flex-start" }}>
          <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> Back to communities
        </button>

        {c.loading && <CardSkeleton height={120} label="Loading…" />}
        {c.error && <CardError message={c.error} height={120} />}
        {c.data && (
          <div className="card card--pad-lg">
            <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
              <div>
                <div className="row" style={{ gap: 8, marginBottom: 6 }}>
                  <span className={`pill ${TIER_PILL[c.data.subscription_tier] || "pill--ghost"}`}>{tierLabel(c.data.subscription_tier)}</span>
                  <span className="pill pill--ghost mono">{c.data.slug}</span>
                  {!c.data.is_active && <span className="pill pill--ghost">Deactivated</span>}
                </div>
                <h2 className="display" style={{ fontSize: 28, fontWeight: 400, letterSpacing: "-0.02em" }}>{c.data.name}</h2>
                <div className="text-sm text-mute" style={{ marginTop: 4 }}>
                  {fmtNum(c.data.user_count)} members · {fmtNum(c.data.active_bylaw_count)} active bylaws
                  {c.data.subscription_status && <> · sub <span className="mono">{c.data.subscription_status}</span></>}
                </div>
              </div>
              {c.data.is_active && (
                <div className="row" style={{ gap: 8 }}>
                  <button
                    className="btn btn--ghost"
                    onClick={deactivateCommunity}
                    disabled={busy}
                    style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                  >
                    {busy ? "Deactivating…" : (confirmDelete ? "Confirm — deactivate community" : "Deactivate community")}
                  </button>
                  {confirmDelete && !busy && (
                    <button
                      className="btn btn--ghost"
                      onClick={() => { setConfirmDelete(false); setActionError(null); }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
              {!c.data.is_active && (
                <div className="row" style={{ gap: 8 }}>
                  <button
                    className="btn btn--ghost"
                    onClick={reactivateCommunity}
                    disabled={busy || permDeleteOpen}
                    style={{ color: "var(--accent-ink)" }}
                  >
                    {busy ? "Restoring…" : "Restore community"}
                  </button>
                  {!permDeleteOpen && (
                    <button
                      className="btn btn--ghost"
                      onClick={() => { setPermDeleteOpen(true); setPermDeleteInput(""); setActionError(null); }}
                      disabled={busy}
                      style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                    >
                      Delete permanently
                    </button>
                  )}
                </div>
              )}
            </div>
            {confirmDelete && (
              <div className="text-sm text-mute" style={{ marginTop: 10 }}>
                This will deactivate every member's account, hide the community from listings, and cancel any active Stripe subscription. Data is preserved.
              </div>
            )}
            {permDeleteOpen && (
              <div style={{
                marginTop: 14, padding: "12px 14px", borderRadius: 8,
                background: "var(--danger-soft, #fdecea)",
                border: "1px solid var(--danger, #b3261e)",
                color: "var(--danger, #b3261e)",
              }}>
                <div style={{ fontSize: 13.5, marginBottom: 10 }}>
                  <b>Irreversible.</b> This deletes every member account, every bylaw (and its file on Anthropic), every chat session and message, and every usage event. Any Stripe subscription will be canceled. Type <span className="mono" style={{ color: "var(--ink)" }}>DELETE</span> to confirm.
                </div>
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  <input
                    className="input mono"
                    placeholder="DELETE"
                    value={permDeleteInput}
                    onChange={(e) => setPermDeleteInput(e.target.value)}
                    autoFocus
                    style={{ flex: 1, maxWidth: 200 }}
                    disabled={busy}
                  />
                  <button
                    className="btn btn--sm btn--ghost"
                    onClick={() => { setPermDeleteOpen(false); setPermDeleteInput(""); }}
                    disabled={busy}
                  >Cancel</button>
                  <button
                    className="btn btn--sm"
                    onClick={permanentlyDeleteCommunity}
                    disabled={busy || permDeleteInput !== "DELETE"}
                    style={{
                      background: "var(--danger)", color: "#fff",
                      opacity: permDeleteInput === "DELETE" ? 1 : 0.5,
                    }}
                  >
                    {busy ? "Deleting…" : "Permanently delete community"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {actionError && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)", border: "1px solid var(--danger, #b3261e)" }}>
            {actionError}
          </div>
        )}

        {/* Operator-only polish-window adjustment. Hidden until the
            community detail has loaded so we don't render a spinner
            on a community that errored out above. */}
        {c.data && c.data.is_active && (
          <PolishUsageCard communityId={communityId} />
        )}

        <div className="card card--pad-lg">
          <h3 className="display" style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Add member</h3>
          <p className="text-mute text-sm" style={{ marginTop: 4 }}>
            Attach an existing user to this community. The user must already have an account — they can sign up
            with any email and you can then add them here with the role they should have in this HOA.
          </p>
          <form onSubmit={addMember} className="row" style={{ marginTop: 14, gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 240px", minWidth: 220 }}>
              <label className="field__label" style={{ display: "block", marginBottom: 6 }}>Email</label>
              <input
                className="input" type="email" autoComplete="off"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="someone@example.com"
                disabled={inviteBusy}
              />
            </div>
            <div style={{ width: 180 }}>
              <label className="field__label" style={{ display: "block", marginBottom: 6 }}>Role</label>
              <select
                className="select"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                disabled={inviteBusy}
              >
                <option value="homeowner">Homeowner</option>
                <option value="manager">Manager</option>
                <option value="company_admin">Company admin</option>
                <option value="super_admin">Operator</option>
              </select>
            </div>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={inviteBusy || !inviteEmail.trim()}
              style={{ height: 38 }}
            >
              {inviteBusy ? "Adding…" : "Add member"}
            </button>
          </form>
          {inviteError && (
            <div role="alert" style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)", fontSize: 13.5 }}>
              {inviteError}
            </div>
          )}
          {inviteOk && (
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "var(--accent-soft, #eef3e6)", color: "var(--accent-ink, #3d4f2a)", fontSize: 13.5 }}>
              {inviteOk}
            </div>
          )}
        </div>

        {u.loading && <CardSkeleton height={180} label="Loading roster…" />}
        {u.error && <CardError message={u.error} height={180} />}
        {u.data === null || u.users.length > 0 ? (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Tokens</th>
                  <th style={{ textAlign: "right" }}>Spend</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {u.users.map((m) => (
                  <tr key={m.id} style={{ opacity: m.is_active ? 1 : 0.55 }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {m.full_name || "(no name)"}
                        {me && m.id === me.id && <span className="text-xs text-mute mono" style={{ marginLeft: 6 }}>· you</span>}
                      </div>
                      <div className="text-sm text-mute mono">{m.email}</div>
                    </td>
                    <td>
                      {canEditRole(m) ? (
                        <select
                          className="select"
                          value={m.role}
                          onChange={(e) => changeRole(m, e.target.value)}
                          disabled={pendingUserId === m.id}
                          style={{ minWidth: 150, padding: "4px 8px", fontSize: 13 }}
                        >
                          <option value="homeowner">Homeowner</option>
                          <option value="manager">Manager</option>
                          <option value="company_admin">Company admin</option>
                          <option value="super_admin">Operator</option>
                        </select>
                      ) : (
                        <span className="text-sm">{m.role}</span>
                      )}
                    </td>
                    <td>
                      {m.is_active
                        ? <span className="pill pill--green"><span className="pill__dot" /> Active</span>
                        : <span className="pill pill--ghost">Deactivated</span>
                      }
                    </td>
                    <td className="mono text-sm" style={{ textAlign: "right", color: "var(--mute)" }}>
                      {fmtTokensCompact(m.usage?.total_tokens)}
                    </td>
                    <td className="mono text-sm" style={{ textAlign: "right" }}>
                      {fmtUsdCompact(m.usage?.estimated_cost_usd)}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {canRemove(m) && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => removeMember(m)}
                          disabled={pendingUserId === m.id}
                          title={me && m.id === me.id ? "Remove yourself from this community (console access stays intact)" : "Remove from this community (account stays intact)"}
                          style={{ marginRight: 4 }}
                        >
                          {pendingUserId === m.id ? "…" : (me && m.id === me.id ? "Remove me" : "Remove")}
                        </button>
                      )}
                      {canDeactivateRow(m) && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => deactivateUser(m)}
                          disabled={pendingUserId === m.id}
                          style={{ color: "var(--danger)", marginRight: 4 }}
                          title="Deactivate the account globally (chat history wiped, login disabled)"
                        >
                          {pendingUserId === m.id ? "…" : "Deactivate"}
                        </button>
                      )}
                      {canReactivateRow(m) && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => reactivateUser(m)}
                          disabled={pendingUserId === m.id}
                          style={{ marginRight: 4 }}
                          title="Re-enable login for this account"
                        >
                          {pendingUserId === m.id ? "…" : "Reactivate"}
                        </button>
                      )}
                      {canDeleteAccount(m) && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => deleteAccount(m)}
                          disabled={pendingUserId === m.id}
                          style={{ color: "var(--danger)" }}
                          title="Permanently delete the account from the platform"
                        >
                          {pendingUserId === m.id ? "…" : "Delete"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !u.loading && !u.error && <CardEmpty message="No members." height={120} />
        )}
      </div>
    </div>
  );
}

/* ─── Revenue ────────────────────────────────────────────────────────────── */
function AdminRevenue() {
  const rv = useDashboardCall(() => Api.dashboardRevenue());
  const ov = useDashboardCall(() => Api.dashboardOverview());

  return (
    <div className="scroll">
      <div className="stack-lg">
        <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
          <Stat label="MRR" value={ov.loading ? "…" : (ov.data ? fmtCentsAsUSD(ov.data.revenue.monthly_recurring_cents) : "—")} />
          <Stat
            label="ARR (annualized)"
            value={ov.loading ? "…" : (ov.data ? fmtCentsAsUSD((ov.data.revenue.monthly_recurring_cents || 0) * 12) : "—")}
          />
          <Stat label="Communities" value={ov.loading ? "…" : (ov.data ? fmtNum(ov.data.communities) : "—")} />
        </div>

        <div className="card">
          <div className="card__hd">
            <div className="card__title">Current MRR by tier</div>
            <span className="text-sm text-mute">snapshot</span>
          </div>
          {rv.loading && <div style={{ padding: 24, textAlign: "center", color: "var(--mute)" }}>Loading…</div>}
          {rv.error && <div style={{ padding: 24, textAlign: "center", color: "var(--danger)" }}>{rv.error}</div>}
          {rv.data && (
            (rv.data.by_tier || []).length === 0
              ? <div style={{ padding: 24, textAlign: "center", color: "var(--mute)" }}>No revenue yet.</div>
              : (
                <table className="table">
                  <thead><tr><th>Tier</th><th>Communities</th><th>MRR</th><th>Avg / community</th></tr></thead>
                  <tbody>
                    {rv.data.by_tier.map((r) => (
                      <tr key={r.tier}>
                        <td><span className={`pill ${TIER_PILL[r.tier] || "pill--ghost"}`}>{tierLabel(r.tier)}</span></td>
                        <td className="mono">{r.communities}</td>
                        <td className="mono">{fmtCentsAsUSD(r.monthly_revenue_cents)}</td>
                        <td className="mono">
                          {r.communities ? fmtCentsAsUSD(r.monthly_revenue_cents / r.communities) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          )}
          <div className="text-xs text-mute" style={{ padding: 12 }}>
            Historical MRR trends and margin-vs-cost charts will appear here once the backend records monthly revenue snapshots.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Trends ─────────────────────────────────────────────────────────────── */
function AdminTrends() {
  const [days, setDays] = useStateA(30);
  const tr = useDashboardCall(() => Api.dashboardTrends({ days }), [days]);

  return (
    <div className="scroll">
      <div className="stack-lg">
        <div className="card">
          <div className="card__hd">
            <div className="card__title">System-wide activity · last {days} days</div>
            <div className="top__role" style={{ background: "var(--bg-2)", border: 0 }}>
              {[7, 30, 90, 365].map((d) => (
                <button key={d} className={d === days ? "is-active" : ""} onClick={() => setDays(d)}>
                  {d === 365 ? "12m" : `${d}d`}
                </button>
              ))}
            </div>
          </div>
          <TrendsBlock state={tr} />
          {tr.data && (
            <div className="row" style={{ gap: 24, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--hairline)", fontSize: 12.5 }}>
              <TrendTotal label="Chat events" value={fmtNum((tr.data.series || []).reduce((a, d) => a + d.chat_events, 0))} />
              <TrendTotal label="Input tokens" value={fmtTokens((tr.data.series || []).reduce((a, d) => a + d.input_tokens, 0))} />
              <TrendTotal label="Output tokens" value={fmtTokens((tr.data.series || []).reduce((a, d) => a + d.output_tokens, 0))} />
              <TrendTotal label="Cache reads" value={fmtTokens((tr.data.series || []).reduce((a, d) => a + (d.cache_read_input_tokens || 0), 0))} />
              <TrendTotal label="Cache writes" value={fmtTokens((tr.data.series || []).reduce((a, d) => a + (d.cache_creation_input_tokens || 0), 0))} />
              <TrendTotal label="Estimated cost" value={fmtUsdCompact((tr.data.series || []).reduce((a, d) => a + d.estimated_cost_usd, 0))} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TrendTotal({ label, value }) {
  return (
    <div>
      <div className="text-mute">{label}</div>
      <div className="display" style={{ fontSize: 22, fontWeight: 400, marginTop: 2 }}>{value}</div>
    </div>
  );
}

/* ─── All Users (operator) ───────────────────────────────────────────────── */
function AdminUsers({ user: me }) {
  const [state, setState] = useStateA({ loading: true, error: null, users: [], meta: null });
  // Search text — debounced into the server-side `q` filter so a
  // match on page 3 still surfaces when the operator types a name
  // (issue #159). Client-side filtering of just the current slice
  // would re-introduce the orphan-finding regression we're closing.
  const [filter, setFilter] = useStateA("");
  const [orphansOnly, setOrphansOnly] = useStateA(false);
  const [offset, setOffset] = useStateA(0);
  const [pendingId, setPendingId] = useStateA(null);
  const [actionError, setActionError] = useStateA(null);
  // Request-invalidation counter (same pattern as PR #156 browse
  // views): filter/offset changes can queue overlapping reloads;
  // only the newest response is allowed to write state.
  const reqIdRef = useRefA(0);
  // Refs tracking the latest filter/offset/orphansOnly so callbacks
  // deferred past a render (debounced search timer, post-await reload
  // after Delete) read current values instead of stale closures.
  // Round 1 codex P2 (orphansOnly stale inside debounced reload) and
  // round 2 codex P2 (post-delete reload uses click-handler's old
  // render snapshot, can overwrite a fresh paged/filtered view if
  // the operator changes state during the delete RTT) both reduce to
  // the same shape: a callback fires after a render the closure
  // didn't see.
  const orphansOnlyRef = useRefA(orphansOnly);
  orphansOnlyRef.current = orphansOnly;
  const filterRef = useRefA(filter);
  filterRef.current = filter;
  const offsetRef = useRefA(offset);
  offsetRef.current = offset;

  const reload = (nextOffset = offset, opts = {}) => {
    const myReq = ++reqIdRef.current;
    setState((s) => ({ loading: true, error: null, users: [], meta: s.meta }));
    Api.adminListAllUsers({
      offset: nextOffset,
      q: opts.q !== undefined ? opts.q : (filter.trim() || undefined),
      orphans_only: opts.orphans_only !== undefined ? opts.orphans_only : orphansOnly,
    })
      .then(({ items, meta }) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: null, users: items || [], meta: meta || null });
      })
      .catch((err) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: err.message || "Couldn't load users.", users: [], meta: null });
      });
  };

  // Reset to page 1 whenever the orphans-only filter flips; the
  // reload-on-offset effect below picks up the new query.
  useEffectA(() => { setOffset(0); /* eslint-disable-next-line */ }, [orphansOnly]);

  // Search-as-you-type with a 250ms debounce. Mirror PR #156's
  // marketplace pattern — bypass the `filter` closure by passing the
  // current value explicitly, so the request reflects the latest
  // keystroke rather than whatever React batched into state.
  // `orphans_only` reads from a ref because the user may toggle the
  // checkbox between this effect's run and the timer firing — the
  // closure-captured `orphansOnly` would otherwise be stale.
  useEffectA(() => {
    const t = setTimeout(() => {
      setOffset(0);
      reload(0, {
        q: filter.trim() || undefined,
        orphans_only: orphansOnlyRef.current,
      });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filter]);

  useEffectA(() => { reload(offset); /* eslint-disable-next-line */ }, [offset, orphansOnly]);

  const onDelete = async (u) => {
    if (!window.confirm(
      `Permanently delete ${u.full_name || u.email}? This wipes the account, all memberships, and chat history. Bylaw uploads and community events stay intact but no longer show this person as the author. This can NOT be undone.`
    )) return;
    setPendingId(u.id);
    setActionError(null);
    try {
      await Api.adminDeleteUser(u.id);
      // Drop the deleted row from the current page; reload so the
      // pager total + page-fill stay in sync with the server. Read
      // offset/filter/orphansOnly from refs so a mid-flight filter
      // or pager click doesn't get overwritten by this resumed
      // handler's stale closure (codex P2 round 2).
      setState((s) => ({ ...s, users: s.users.filter((x) => x.id !== u.id) }));
      reload(offsetRef.current, {
        q: filterRef.current.trim() || undefined,
        orphans_only: orphansOnlyRef.current,
      });
    } catch (err) {
      setActionError(err.message || "Couldn't delete user.");
    } finally {
      setPendingId(null);
    }
  };

  const rows = state.users;
  const totalAccounts = state.meta ? state.meta.total : state.users.length;

  return (
    <div className="scroll">
      <div className="stack-md">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div className="card" style={{ padding: "8px 12px", flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 220 }}>
            <Icon name="search" size={16} style={{ color: "var(--mute)" }} />
            <input
              className="input"
              aria-label="Search users by name or email"
              placeholder="Search by name or email…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ border: 0, padding: 0, background: "transparent", flex: 1 }}
            />
          </div>
          <label className="row text-sm" style={{ gap: 6, alignItems: "center", cursor: "pointer", whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={orphansOnly}
              onChange={(e) => setOrphansOnly(e.target.checked)}
            />
            Orphans only
          </label>
          <div className="text-sm text-mute" style={{ whiteSpace: "nowrap" }}>
            {totalAccounts} {totalAccounts === 1 ? "account" : "accounts"}
          </div>
        </div>

        {actionError && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)", border: "1px solid var(--danger, #b3261e)" }}>
            {actionError}
          </div>
        )}

        {state.loading && <CardSkeleton height={240} label="Loading users…" />}
        {state.error && <CardError message={state.error} height={240} />}
        {!state.loading && !state.error && rows.length === 0 && (
          <CardEmpty
            message={orphansOnly || filter.trim() ? "No users match the current filter." : "No users yet."}
            height={240}
          />
        )}
        {rows.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Communities</th>
                  <th>Last sign-in</th>
                  <th style={{ textAlign: "right" }}>Tokens</th>
                  <th style={{ textAlign: "right" }}>Spend</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {u.full_name || "(no name)"}
                        {me && u.id === me.id && (
                          <span className="text-xs text-mute mono" style={{ marginLeft: 6 }}>· you</span>
                        )}
                        {!u.is_active && (
                          <span className="pill pill--ghost" style={{ marginLeft: 6 }}>Deactivated</span>
                        )}
                      </div>
                      <div className="text-sm text-mute mono">{u.email}</div>
                    </td>
                    <td>
                      {u.memberships.length === 0 ? (
                        <span className="text-sm text-mute">—</span>
                      ) : (
                        <div className="col" style={{ gap: 4 }}>
                          {u.memberships.map((m) => (
                            <div key={m.community_id} className="row" style={{ gap: 6, alignItems: "center", fontSize: 13 }}>
                              <span style={{ fontWeight: 500 }}>{m.name || `#${m.community_id}`}</span>
                              <span className="text-xs text-mute" style={{ textTransform: "capitalize" }}>
                                {m.role.replace("_", " ")}
                              </span>
                              {m.is_active_community && (
                                <span className="pill pill--green" style={{ fontSize: 10 }}>
                                  <span className="pill__dot" /> active
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-sm" style={{ color: "var(--mute)" }}>{_relTimeAdmin(u.last_login_at)}</td>
                    <td className="mono text-sm" style={{ textAlign: "right", color: "var(--mute)" }}>
                      {fmtTokensCompact(u.activity?.total_tokens)}
                    </td>
                    <td className="mono text-sm" style={{ textAlign: "right" }}>
                      {fmtUsdCompact(u.activity?.estimated_cost_usd)}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {(!me || u.id !== me.id) && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => onDelete(u)}
                          disabled={pendingId === u.id}
                          style={{ color: "var(--danger)" }}
                          title="Permanently delete this account"
                        >
                          {pendingId === u.id ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <window.Pager meta={state.meta} offset={offset} onChange={setOffset} busy={state.loading} />
      </div>
    </div>
  );
}

function _relTimeAdmin(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 30) return `${day} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/* ─── AdminHealth ────────────────────────────────────────────────────────── */
// Operator-only "is everything OK" panel. Three cards from one
// /api/admin/health/snapshot round-trip: system (deploy, uptime,
// migration sync), queue depths (background-job backlog), and
// worklist (operator-actionable items only super-admin can clear).

function _fmtDuration(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function HealthMetricRow({ label, value, tone }) {
  // tone: "ok" | "warn" | "muted" | undefined
  const colorMap = {
    ok: "var(--accent-ink, #3d4f2a)",
    warn: "var(--danger, #b3261e)",
    muted: "var(--mute)",
  };
  return (
    <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
      <span className="text-sm text-mute">{label}</span>
      <span
        className="mono"
        style={{ fontSize: 14, color: tone ? colorMap[tone] : "var(--ink)" }}
      >
        {value}
      </span>
    </div>
  );
}

function HealthCountRow({ label, count, attentionThreshold = 1 }) {
  const tone = count >= attentionThreshold ? "warn" : "ok";
  return <HealthMetricRow label={label} value={(count || 0).toLocaleString()} tone={tone} />;
}

function _fmtRelTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const ageSec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (ageSec < 60) return "just now";
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
  return `${Math.floor(ageSec / 86400)}d ago`;
}

function JobRow({ entry }) {
  const latest = entry.latest;
  const t = entry.last_24h || { success: 0, failed: 0, running: 0 };
  const tally = `24h: ${t.success}✓ / ${t.failed}✕${t.running ? ` / ${t.running}●` : ""}`;
  const right = latest
    ? `${latest.status} · ${_fmtRelTime(latest.started_at)} · ${tally}`
    : `never run · ${tally}`;
  return (
    <div
      className="row"
      style={{ justifyContent: "space-between", alignItems: "baseline", gap: 12 }}
    >
      <span className="mono text-sm" style={{ wordBreak: "break-all" }}>{entry.name}</span>
      <span
        className="mono"
        style={{ fontSize: 12, color: latest ? undefined : "var(--mute)", textAlign: "right" }}
      >
        {right}
      </span>
    </div>
  );
}

function AdminHealth() {
  // One useDashboardCall per endpoint (snapshot, jobs). Codex P2 on
  // PR #328 flagged DUAL hooks calling the SAME endpoint; two hooks
  // hitting two different endpoints is fine — each fires once on
  // mount and once per reloadKey bump.
  const [reloadKey, setReloadKey] = useStateA(0);
  const state = useDashboardCall(
    () => Api.adminHealthSnapshot(),
    [reloadKey],
  );
  const jobsState = useDashboardCall(
    () => Api.adminHealthJobs(),
    [reloadKey],
  );
  const webhooksState = useDashboardCall(
    () => Api.adminHealthWebhooks(),
    [reloadKey],
  );
  const aiUsageState = useDashboardCall(
    () => Api.adminHealthAiUsage(),
    [reloadKey],
  );

  return (
    <div className="scroll">
      <div className="stack-lg">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <div className="text-sm text-mute">
            Operator-only snapshot. All numbers are point-in-time; click Refresh after taking action.
          </div>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={state.loading}
          >
            {state.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {state.loading && <CardSkeleton height={240} label="Loading health snapshot…" />}
        {state.error && <CardError message={state.error} height={240} />}
        {state.data && (
          <>
            {/* System */}
            <div className="card card--pad-lg">
              <div className="card__title" style={{ marginBottom: 12 }}>System</div>
              <div className="col" style={{ gap: 8 }}>
                <HealthMetricRow
                  label="Deploy commit"
                  value={state.data.system.deploy_sha || "unknown"}
                  tone={state.data.system.deploy_sha ? undefined : "muted"}
                />
                <HealthMetricRow
                  label="App uptime"
                  value={_fmtDuration(state.data.system.app_uptime_seconds)}
                />
                <HealthMetricRow
                  label="Droplet uptime"
                  value={_fmtDuration(state.data.system.droplet_uptime_seconds)}
                  tone={state.data.system.droplet_uptime_seconds === null ? "muted" : undefined}
                />
                <HealthMetricRow
                  label="DB migration"
                  value={
                    state.data.system.migration.in_sync === true
                      ? `${state.data.system.migration.current} (in sync)`
                      : state.data.system.migration.in_sync === false
                        ? `${state.data.system.migration.current || "?"} → head ${state.data.system.migration.head || "?"}`
                        : "unknown"
                  }
                  tone={
                    state.data.system.migration.in_sync === true ? "ok"
                      : state.data.system.migration.in_sync === false ? "warn"
                      : "muted"
                  }
                />
              </div>
            </div>

            {/* Queues */}
            <div className="card card--pad-lg">
              <div className="card__title" style={{ marginBottom: 12 }}>Background queues</div>
              <div className="col" style={{ gap: 8 }}>
                <HealthCountRow
                  label="Pending Anthropic file deletions"
                  count={state.data.queues.pending_anthropic_deletions}
                  attentionThreshold={50}
                />
                <HealthCountRow
                  label="Bylaw docs awaiting Voyage indexing"
                  count={state.data.queues.pending_bylaw_indexing}
                  attentionThreshold={1}
                />
              </div>
              <div className="text-xs text-mute" style={{ marginTop: 10 }}>
                Both queues are drained by the per-minute sweepers
                installed via <span className="mono">/etc/cron.d/communitycompass</span>.
                Persistent non-zero numbers usually mean the sweeper
                isn't running.
              </div>
            </div>

            {/* Worklist */}
            <div className="card card--pad-lg">
              <div className="card__title" style={{ marginBottom: 12 }}>Operator worklist</div>
              <div className="col" style={{ gap: 8 }}>
                <HealthCountRow
                  label="Unit-count change requests pending"
                  count={state.data.worklist.unit_count_change_requests_pending}
                />
                <HealthCountRow
                  label="Orphan users (active, zero memberships)"
                  count={state.data.worklist.orphan_users}
                />
                <HealthCountRow
                  label="Active communities with no bylaws (chat broken)"
                  count={state.data.worklist.communities_no_active_bylaws}
                />
                <HealthCountRow
                  label="Households with no primary set"
                  count={state.data.worklist.households_no_primary}
                />
                <HealthCountRow
                  label="Trials expiring ≤3d (uncovered by PMC)"
                  count={state.data.worklist.trial_expiring_uncovered}
                />
              </div>
              <div className="text-xs text-mute" style={{ marginTop: 10 }}>
                Every line here is something only the operator can resolve.
                A red number means at least one item needs attention.
              </div>
            </div>
          </>
        )}

        {/* Background jobs */}
        <div className="card card--pad-lg">
          <div className="card__title" style={{ marginBottom: 12 }}>Background jobs</div>
          {jobsState.loading && <CardSkeleton height={160} label="Loading jobs…" />}
          {jobsState.error && <CardError message={jobsState.error} height={160} />}
          {jobsState.data && (
            <>
              <div className="col" style={{ gap: 8 }}>
                {jobsState.data.summary.map((entry) => (
                  <JobRow key={entry.name} entry={entry} />
                ))}
              </div>
              <div className="text-xs text-mute" style={{ marginTop: 10 }}>
                Per-job latest run + last-24h success/failure tally. A job
                that's listed but never-run usually means the cron line
                isn't installed on the droplet.
              </div>
              {jobsState.data.recent.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div className="text-sm text-mute" style={{ marginBottom: 6 }}>
                    Recent activity
                  </div>
                  <div className="col" style={{ gap: 6 }}>
                    {jobsState.data.recent.slice(0, 20).map((run) => (
                      <div
                        key={run.id}
                        className="row"
                        style={{ justifyContent: "space-between", alignItems: "baseline", gap: 12 }}
                      >
                        <span className="mono text-sm">{run.name}</span>
                        <span
                          className="mono"
                          style={{
                            fontSize: 12,
                            color:
                              run.status === "FAILED"
                                ? "var(--danger, #b3261e)"
                                : run.status === "RUNNING"
                                  ? "var(--mute)"
                                  : "var(--accent-ink, #3d4f2a)",
                            textAlign: "right",
                          }}
                        >
                          {run.status} · {_fmtRelTime(run.started_at)}
                          {run.duration_seconds != null
                            ? ` · ${_fmtDuration(run.duration_seconds)}`
                            : ""}
                          {run.message ? ` · ${run.message}` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Stripe webhooks */}
        <div className="card card--pad-lg">
          <div className="card__title" style={{ marginBottom: 12 }}>Stripe webhooks</div>
          {webhooksState.loading && <CardSkeleton height={160} label="Loading webhook activity…" />}
          {webhooksState.error && <CardError message={webhooksState.error} height={160} />}
          {webhooksState.data && (
            <>
              <div className="col" style={{ gap: 8 }}>
                {Object.entries(webhooksState.data.summary.last_24h).map(([status, count]) => (
                  <HealthMetricRow
                    key={status}
                    label={`${status} (24h)`}
                    value={count.toLocaleString()}
                    tone={
                      status === "PROCESSED"
                        ? (count > 0 ? "ok" : "muted")
                        : (count > 0 ? "warn" : "muted")
                    }
                  />
                ))}
              </div>
              <div className="text-xs text-mute" style={{ marginTop: 10 }}>
                If <span className="mono">PROCESSED</span> is 0 over 24h
                but you'd expect traffic, Stripe likely can't reach the
                endpoint — check the Stripe dashboard's webhook log.
                Non-zero <span className="mono">INVALID_SIGNATURE</span>
                {" "}usually means <span className="mono">STRIPE_WEBHOOK_SECRET</span>
                {" "}is stale.
              </div>
              {webhooksState.data.recent.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div className="text-sm text-mute" style={{ marginBottom: 6 }}>
                    Recent activity
                  </div>
                  <div className="col" style={{ gap: 6 }}>
                    {webhooksState.data.recent.slice(0, 20).map((e) => (
                      <div
                        key={e.id}
                        className="row"
                        style={{ justifyContent: "space-between", alignItems: "baseline", gap: 12 }}
                      >
                        <span className="mono text-sm" style={{ wordBreak: "break-all" }}>
                          {e.event_type || "—"}
                        </span>
                        <span
                          className="mono"
                          style={{
                            fontSize: 12,
                            color:
                              e.status === "FAILED" || e.status === "INVALID_SIGNATURE" || e.status === "NOT_CONFIGURED"
                                ? "var(--danger, #b3261e)"
                                : "var(--accent-ink, #3d4f2a)",
                            textAlign: "right",
                          }}
                        >
                          {e.status} · {_fmtRelTime(e.received_at)}
                          {e.latency_ms != null ? ` · ${e.latency_ms}ms` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* AI usage + cost */}
        <div className="card card--pad-lg">
          <div className="card__title" style={{ marginBottom: 12 }}>AI usage + cost</div>
          {aiUsageState.loading && <CardSkeleton height={160} label="Loading AI usage…" />}
          {aiUsageState.error && <CardError message={aiUsageState.error} height={160} />}
          {aiUsageState.data && (
            <>
              <div className="col" style={{ gap: 8 }}>
                <HealthMetricRow
                  label="This month (cost)"
                  value={_fmtUSD(aiUsageState.data.current_month.estimated_cost_usd)}
                />
                <HealthMetricRow
                  label="This month (tokens)"
                  value={_fmtCount(aiUsageState.data.current_month.total_tokens)}
                />
                <HealthMetricRow
                  label="Last month (cost)"
                  value={_fmtUSD(aiUsageState.data.previous_month.estimated_cost_usd)}
                  tone="muted"
                />
                <HealthMetricRow
                  label="Last month (tokens)"
                  value={_fmtCount(aiUsageState.data.previous_month.total_tokens)}
                  tone="muted"
                />
              </div>

              {aiUsageState.data.by_purpose.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div className="text-sm text-mute" style={{ marginBottom: 6 }}>
                    By purpose (this month)
                  </div>
                  <div className="col" style={{ gap: 6 }}>
                    {aiUsageState.data.by_purpose.map((p) => (
                      <div
                        key={p.purpose}
                        className="row"
                        style={{ justifyContent: "space-between", alignItems: "baseline", gap: 12 }}
                      >
                        <span className="mono text-sm">{p.purpose}</span>
                        <span className="mono" style={{ fontSize: 12, textAlign: "right" }}>
                          {_fmtUSD(p.estimated_cost_usd)} · {_fmtCount(p.tokens)} tok
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiUsageState.data.top_communities.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div className="text-sm text-mute" style={{ marginBottom: 6 }}>
                    Top communities (this month)
                  </div>
                  <div className="col" style={{ gap: 6 }}>
                    {aiUsageState.data.top_communities.map((c) => (
                      <div
                        key={c.community_id}
                        className="row"
                        style={{ justifyContent: "space-between", alignItems: "baseline", gap: 12 }}
                      >
                        <span className="text-sm" style={{ wordBreak: "break-all" }}>{c.name}</span>
                        <span className="mono" style={{ fontSize: 12, textAlign: "right" }}>
                          {_fmtUSD(c.estimated_cost_usd)} · {_fmtCount(c.tokens)} tok
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function _fmtUSD(amount) {
  if (amount == null) return "—";
  return `$${Number(amount).toFixed(2)}`;
}

function _fmtCount(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}


export { AdminOverview, AdminHealth, AdminCommunities, AdminRevenue, AdminTrends, AdminUsers };
